import {
  DeviceContext,
  RuntimeContextProvider,
  UserContext,
  EventTrackingContext,
  Identities,
  PageContext,
} from '@mmt/events-schema'
import {
  getLocaleSettings,
  getMarketingCloudVisitorID,
  getProfileType,
  getUserFromStorage,
  isBrowserEnv,
  readCookieValue,
  LoginInfo,
  PROFILE_TYPE_MAP,
  isGI,
} from './utils'
import { getDeviceInfo } from './device-info-provider'
import { Org, OrgValue } from '../../constants'

const NOT_AVAILABLE = 'NA'

function getTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (e) {
    return NOT_AVAILABLE
  }
}

function assertBrowserEnv() {
  if (!isBrowserEnv()) {
    throw new Error('only browser environment not supported')
  }
}

export const runtimeContextProvider: RuntimeContextProvider = {
  getDeviceContext(): DeviceContext {
    assertBrowserEnv()
    const {
      os,
      osVersion,
      networkType,
      version,
      browser,
      platform,
      deviceId,
      model,
      manufacturer,
      userAgent,
      acquired_country,
    } = getDeviceInfo(window)
    return {
      app_ver: version,
      vendor_id: browser,
      device_id: deviceId,
      os,
      os_version: osVersion,
      network_type: networkType,
      platform,
      time_zone: getTimeZone(),
      build_ver: NOT_AVAILABLE,
      ip_addr: NOT_AVAILABLE,
      model,
      manufacturer,
      user_agent: userAgent,
      acquired_country,
    }
  },
  getUserContext(org): UserContext {
    assertBrowserEnv()
    const auth =
      readCookieValue(document.cookie, 'mmt-auth') || readCookieValue(document.cookie, 'OAUTH-GOIBIBO') || NOT_AVAILABLE
    const giUuid = readCookieValue(document.cookie, 'gitokenid') || undefined
    const mmtUserDet = getUserFromStorage()
    const marketingCloudId = getMarketingCloudVisitorID()

    if (isGI(org)) {
      return {
        uuid: giUuid,
        profile_type: PROFILE_TYPE_MAP.b2c,
        is_logged_in: auth !== NOT_AVAILABLE,
        marketing_cloud_id: marketingCloudId ?? 'NA',
        login_channel: OrgValue.GI,
        login_type: 'OTP',
        nationality: null,
        loyalty_status: null,
        loyalty_type: null,
        loyalty_level: null,
        wallet_bal: null,
      }
    } else {
      let userContext: Partial<UserContext> = {}
      const wallet = localStorage.getItem('walletInfo')
      if (wallet) {
        const walletData = JSON.parse(wallet)
        const loyaltyData = walletData?.loyaltyData || {}
        userContext = {
          ...userContext,
          loyalty_status: loyaltyData?.status,
          loyalty_type: loyaltyData?.program,
          loyalty_level: loyaltyData?.currentTier?.name,
          wallet_bal: walletData?.multiCurrencyDetails?.totalWalletAmount || 0,
        }
      }
      const loginChannel = localStorage.getItem('channel')
      if (loginChannel) {
        userContext = {
          ...userContext,
          login_channel: loginChannel,
        }
      }
      const loginType = localStorage.getItem('loginType')
      if (loginType) {
        userContext = {
          ...userContext,
          login_type: loginType,
        }
      }
      return {
        uuid: mmtUserDet?.uuid,
        profile_type: getProfileType(),
        is_logged_in: auth !== NOT_AVAILABLE,
        marketing_cloud_id: marketingCloudId ?? 'NA',
        login_channel: userContext.login_channel || null,
        login_type: userContext.login_type || null,
        nationality: mmtUserDet?.nationality,
        loyalty_status: userContext.loyalty_status || null,
        loyalty_type: userContext.loyalty_type || null,
        loyalty_level: userContext.loyalty_level || null,
        wallet_bal: userContext.wallet_bal || 0,
      }
    }
  },

  getEventTrackingContext(): EventTrackingContext {
    assertBrowserEnv()

    const localeSettings = getLocaleSettings()
    const { region, currency, userCurrency, language } = localeSettings

    return {
      template_id: 0,
      topic_name: NOT_AVAILABLE,
      env: NOT_AVAILABLE,
      timezone: NOT_AVAILABLE,
      session_id: NOT_AVAILABLE,
      funnel_source: NOT_AVAILABLE,
      session_visit_number: 0,
      region,
      user_preferred_currency: userCurrency,
      domain_country: region,
      domain_language: language,
      domain_currency: currency,
    }
  },

  getPageContext(_org: Org): PageContext {
    const profileType = getProfileType()
    if (profileType === PROFILE_TYPE_MAP.b2c) {
      return {
        travel_store: _org === OrgValue.GI ? 'GI' : 'MMT',
      }
    }
    return {}
  },

  getUserIdentities(org: Org): Identities {
    const mmtUserDet = getUserFromStorage()

    if (org === OrgValue.GI) {
      return {}
    }

    let phone_number_hash: string | undefined
    let email_address_hash: string | undefined

    if (Array.isArray(mmtUserDet?.loginInfoList)) {
      const mobileEntry = mmtUserDet.loginInfoList.find(
        (entry: LoginInfo) => entry.loginType === 'MOBILE' && entry.commId,
      )
      const emailEntry = mmtUserDet.loginInfoList.find(
        (entry: LoginInfo) => entry.loginType === 'EMAIL' && entry.commId,
      )
      if (mobileEntry) phone_number_hash = mobileEntry.commId
      if (emailEntry) email_address_hash = emailEntry.commId
    }

    const identities: Identities = {}
    if (phone_number_hash) identities.phone_number_hash = phone_number_hash
    if (email_address_hash) identities.email_address_hash = email_address_hash
    return identities
  },
}
